package vegasful.admin.views

import androidx.compose.runtime.*
import com.steamstreet.graphkt.asOptionalInput
import kotlinx.coroutines.delay
import kotlinx.datetime.Clock
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import vegasful.admin.Application
import vegasful.admin.admin
import vegasful.admin.api.SocialImageDateStyle
import vegasful.admin.api.SocialImageRenderer
import vegasful.admin.api.SocialImageState
import vegasful.admin.components.*
import kotlin.time.Duration.Companion.minutes

/**
 * Options that affect what generateSocialImage produces. Mirror the
 * server-side args on the mutation. `renderer` is required by the server
 * (no schema default), but the UI defaults to TEMPLATE here so the form
 * has a reasonable starting state — the admin can switch to one of the
 * generative renderers explicitly.
 */
data class SlideOptions(
    val renderer: SocialImageRenderer = SocialImageRenderer.TEMPLATE,
    val dateStyle: SocialImageDateStyle = SocialImageDateStyle.DEFAULT,
    val omitTime: Boolean = false,
    val includeTagline: Boolean = true,
    val includeVenue: Boolean = true,
    /** Bypass the template renderer's wide-aspect letterbox heuristic. */
    val forceCover: Boolean = false,
)

/**
 * UI state machine for the social image page.
 *
 * `SocialImageState` (the GraphQL enum used on the wire — PENDING/READY/FAILED)
 * lives in `vegasful.admin.api`. This `ViewState` is the local rendering state
 * machine and intentionally distinct.
 */
internal sealed class ViewState {
    object Idle : ViewState()
    object Submitting : ViewState()
    data class Polling(val futureUrl: String) : ViewState()
    data class Ready(val url: String) : ViewState()
    data class Failed(val message: String) : ViewState()
}

/**
 * Poll loop. Calls `socialImageStatus(url)` via GraphQL on the admin endpoint
 * until the server reports READY or FAILED, or the 60s deadline elapses.
 *
 * The server-side resolver does an S3 HEAD on the PNG and (on miss) a GET on
 * the `.error.txt` sibling, so we don't need to touch the CDN ourselves —
 * which sidesteps the CORS issue browsers hit on `media.vegasful.com`.
 *
 * Cancellation is handled by the LaunchedEffect that wraps this — when the
 * key changes (Regenerate click) or the composition leaves, the coroutine
 * is cancelled and any in-flight GraphQL call is abandoned.
 */
internal suspend fun pollForImage(eventId: String, futureUrl: String): ViewState {
    val deadline = Clock.System.now() + 5.minutes
    var delayMs = 2_000L

    while (Clock.System.now() < deadline) {
        val status = Application.mutate {
            admin {
                events {
                    event(eventId) {
                        socialImageStatus(futureUrl) {
                            this.state
                            this.url
                            this.error
                        }
                    }
                }
            }
        }.admin.events.event.socialImageStatus

        when (status.state) {
            SocialImageState.READY -> {
                return ViewState.Ready(status.url ?: futureUrl)
            }
            SocialImageState.FAILED -> {
                val msg = status.error?.takeIf { it.isNotBlank() } ?: "Unknown error"
                return ViewState.Failed("Generation failed: $msg")
            }
            SocialImageState.PENDING -> {
                delay(delayMs)
                delayMs = minOf((delayMs * 3) / 2, 5_000L)
            }
            is SocialImageState.Unknown -> {
                return ViewState.Failed("Unrecognized image state: ${status.state}")
            }
        }
    }

    return ViewState.Failed(
        "Timed out after 5 minutes — server may have restarted, try Regenerate"
    )
}

internal suspend fun startGeneration(eventId: String, options: SlideOptions = SlideOptions()): String {
    return Application.mutate {
        admin {
            events {
                event(eventId) {
                    generateSocialImage(
                        renderer = options.renderer,
                        dateStyle = options.dateStyle.asOptionalInput(),
                        omitTime = options.omitTime.asOptionalInput(),
                        includeTagline = options.includeTagline.asOptionalInput(),
                        includeVenue = options.includeVenue.asOptionalInput(),
                        forceCover = options.forceCover.asOptionalInput(),
                    )
                }
            }
        }
    }.admin.events.event.generateSocialImage
}

/**
 * Extract a download-friendly filename from the future URL.
 * e.g. https://media.vegasful.com/social/abc-def.png → vegasful-social-abc-def.png
 */
internal fun downloadFilenameFor(url: String): String {
    val basename = url.substringAfterLast('/').substringBefore('?')
    return "vegasful-social-$basename"
}

@Composable
fun socialImage(eventId: String) {
    // Header data — just enough for context (name/venue/date/time).
    var eventName: String? by remember { mutableStateOf(null) }
    var venueName: String? by remember { mutableStateOf(null) }
    var eventDate: String? by remember { mutableStateOf(null) }
    var eventTime: String? by remember { mutableStateOf(null) }

    LaunchedEffect(eventId) {
        eventName = null
        venueName = null
        eventDate = null
        eventTime = null

        // Read just the resolved header fields off the override → base → AI
        // fallback chain by simply pulling the layers and picking the first
        // non-null. The Event detail view does the same dance.
        val event = Application.mutate {
            admin {
                events {
                    event(eventId) {
                        this.id
                        layers {
                            this.layerId
                            this.name
                            this.date
                            this.time
                            this.venue {
                                this.name
                            }
                        }
                    }
                }
            }
        }.admin.events.event

        eventName = event.layers.firstNotNullOfOrNull { it.name }
        venueName = event.layers.firstNotNullOfOrNull { it.venue?.name }
        eventDate = event.layers.firstNotNullOfOrNull { it.date }
        eventTime = event.layers.firstNotNullOfOrNull { it.time }
    }

    var state: ViewState by remember { mutableStateOf(ViewState.Idle) }
    var options by remember { mutableStateOf(SlideOptions()) }
    // Bumping this counter triggers a new generation; LaunchedEffect keyed on
    // it cancels any prior poll loop.
    var generationCounter by remember { mutableStateOf(0) }
    // Capture the options used for the in-flight generation so the user can
    // continue editing the form while the previous job is running.
    var inFlightOptions by remember { mutableStateOf(SlideOptions()) }

    LaunchedEffect(generationCounter) {
        if (generationCounter == 0) return@LaunchedEffect
        state = ViewState.Submitting
        try {
            val futureUrl = startGeneration(eventId, inFlightOptions)
            state = ViewState.Polling(futureUrl)
            state = pollForImage(eventId, futureUrl)
        } catch (t: Throwable) {
            val msg = t.message ?: "Unknown error"
            state = ViewState.Failed("Failed to start generation: $msg")
            Application.notifications.showError(msg)
        }
    }

    val startGenerating = {
        inFlightOptions = options
        generationCounter += 1
    }

    contentContainer {
        entityView {
            title = eventName ?: "Social Image"

            val subBits = listOfNotNull(
                venueName,
                listOfNotNull(eventDate, eventTime).joinToString(" ").takeIf { it.isNotBlank() }
            )
            subTitle = subBits.takeIf { it.isNotEmpty() }?.joinToString(" • ")

            breadcrumbs {
                crumb("Events", "content/events") {
                    crumb(eventName ?: eventId, "content/events/$eventId")
                }
            }

            content {
                box("Social Image") {
                    val currentState = state
                    when (currentState) {
                        ViewState.Idle -> {
                            Div({ style { marginBottom(12.px) } }) {
                                Text(
                                    "Generate a Vegasful-branded social media slide for this " +
                                        "event. Generation can take up to a few minutes. Each " +
                                        "call produces a new variation."
                                )
                            }
                            slideOptionsForm(options) { options = it }
                            Div {
                                button("Generate", primary = true) {
                                    startGenerating()
                                }
                            }
                        }

                        ViewState.Submitting -> {
                            statusRow("Starting generation…")
                        }

                        is ViewState.Polling -> {
                            statusRow("Generating image… (can take a few minutes)")
                        }

                        is ViewState.Ready -> {
                            readyView(
                                url = currentState.url,
                                options = options,
                                onOptionsChange = { options = it },
                                onRegenerate = { startGenerating() }
                            )
                        }

                        is ViewState.Failed -> {
                            failureView(
                                message = currentState.message,
                                options = options,
                                onOptionsChange = { options = it },
                                onRetry = { startGenerating() }
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun statusRow(message: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            property("gap", "12px")
        }
    }) {
        spinner()
        Span { Text(message) }
    }
}

@Composable
private fun readyView(
    url: String,
    options: SlideOptions,
    onOptionsChange: (SlideOptions) -> Unit,
    onRegenerate: () -> Unit
) {
    Div({ style { marginBottom(16.px) } }) {
        Img(src = url) {
            attr("alt", "Generated social image")
            style {
                maxWidth(600.px)
                property("width", "100%")
                property("height", "auto")
                property("border-radius", "4px")
                property("box-shadow", "0 2px 8px rgba(0,0,0,.15)")
            }
        }
    }
    slideOptionsForm(options, onOptionsChange)
    Div({
        style {
            display(DisplayStyle.Flex)
            property("gap", "10px")
            alignItems(AlignItems.Center)
        }
    }) {
        A(href = url, attrs = {
            attr("download", downloadFilenameFor(url))
            classes(ButtonStyles.button, ButtonStyles.primaryButton)
            style {
                property("text-decoration", "none")
            }
        }) {
            Text("Download")
        }
        button("Regenerate") {
            onRegenerate()
        }
        Span({
            style {
                fontSize((0.8).cssRem)
                opacity(.7)
                marginLeft(8.px)
            }
        }) {
            A(href = url, attrs = { attr("target", "_blank") }) {
                Text("Open in new tab")
            }
        }
    }
}

@Composable
private fun failureView(
    message: String,
    options: SlideOptions,
    onOptionsChange: (SlideOptions) -> Unit,
    onRetry: () -> Unit
) {
    Div({
        style {
            marginBottom(12.px)
            color(Color("#b00020"))
        }
    }) {
        Text(message)
    }
    slideOptionsForm(options, onOptionsChange)
    Div {
        button("Try again", primary = true) {
            onRetry()
        }
    }
}

/**
 * Reusable form for the four slide-generation options. Shared by the single
 * social-image page and the Today batch page so the rendered controls stay
 * consistent.
 */
@Composable
internal fun slideOptionsForm(
    options: SlideOptions,
    onChange: (SlideOptions) -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            property("gap", "8px")
            margin(12.px, 0.px)
            padding(12.px)
            backgroundColor(Color("#f7f7f7"))
            property("border-radius", "4px")
            fontSize((0.9).cssRem)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                property("gap", "8px")
            }
        }) {
            Span { Text("Renderer:") }
            Select({
                onChange { evt ->
                    val raw = evt.value ?: return@onChange
                    onChange(options.copy(renderer = SocialImageRenderer.valueOf(raw)))
                }
            }) {
                SocialImageRenderer.entries.forEach { r ->
                    Option(r.name, {
                        if (options.renderer == r) selected()
                    }) {
                        Text(rendererLabel(r))
                    }
                }
            }
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                property("gap", "8px")
            }
        }) {
            Span { Text("Date style:") }
            Select({
                onChange { evt ->
                    val raw = evt.value ?: return@onChange
                    onChange(options.copy(dateStyle = SocialImageDateStyle.valueOf(raw)))
                }
            }) {
                SocialImageDateStyle.entries.forEach { style ->
                    Option(style.name, {
                        if (options.dateStyle == style) selected()
                    }) {
                        Text(dateStyleLabel(style))
                    }
                }
            }
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                property("gap", "20px")
                property("flex-wrap", "wrap")
            }
        }) {
            checkboxRow("Omit time", options.omitTime) {
                onChange(options.copy(omitTime = it))
            }
            checkboxRow("Include tagline", options.includeTagline) {
                onChange(options.copy(includeTagline = it))
            }
            checkboxRow("Include venue", options.includeVenue) {
                onChange(options.copy(includeVenue = it))
            }
            checkboxRow("Force cover (template only)", options.forceCover) {
                onChange(options.copy(forceCover = it))
            }
        }
    }
}

@Composable
private fun checkboxRow(label: String, checked: Boolean, onChange: (Boolean) -> Unit) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            property("gap", "6px")
            cursor("pointer")
        }
    }) {
        Input(InputType.Checkbox) {
            this.checked(checked)
            this.onInput { onChange(it.value) }
        }
        Span { Text(label) }
    }
}

private fun dateStyleLabel(style: SocialImageDateStyle): String = when (style) {
    SocialImageDateStyle.DEFAULT -> "Full date (default)"
    SocialImageDateStyle.TODAY -> "TODAY"
    SocialImageDateStyle.TONIGHT -> "TONIGHT"
    SocialImageDateStyle.DAY_OF_WEEK -> "Day of week"
    is SocialImageDateStyle.Unknown -> style.name
}

private fun rendererLabel(renderer: SocialImageRenderer): String = when (renderer) {
    SocialImageRenderer.TEMPLATE -> "Template (fast, brand)"
    SocialImageRenderer.OPENAI_GPT_IMAGE_2 -> "OpenAI gpt-image-2 (slow, creative)"
    SocialImageRenderer.GEMINI_NANO_BANANA_PRO -> "Gemini nano banana pro (slow, creative)"
    is SocialImageRenderer.Unknown -> renderer.name
}
